import { useEffect, useState, type ReactNode } from "react"
import AuthLoadingScreen from "./AuthLoadingScreen"

const READY_EVENT = "atlas:auth-ready"
const FAILED_EVENT = "atlas:auth-failed"

type AtlasWindow = Window & {
  __atlasAuthReady?: boolean
  __atlasAuthFailed?: boolean
}

function currentState(): "loading" | "ready" | "failed" {
  const atlasWindow = window as AtlasWindow
  if (atlasWindow.__atlasAuthReady) return "ready"
  if (atlasWindow.__atlasAuthFailed) return "failed"
  return "loading"
}

export function AuthBootOverlay() {
  const [state, setState] = useState<"loading" | "ready" | "failed">(currentState)

  useEffect(() => {
    const ready = () => setState("ready")
    const failed = () => setState("failed")
    window.addEventListener(READY_EVENT, ready)
    window.addEventListener(FAILED_EVENT, failed)

    // React may mount the Clerk child before this sibling effect subscribes.
    // Re-read the durable signal so that effect ordering can never strand the overlay.
    setState(currentState())

    return () => {
      window.removeEventListener(READY_EVENT, ready)
      window.removeEventListener(FAILED_EVENT, failed)
    }
  }, [])

  if (state === "ready") return null
  return <div className="fixed inset-0 z-[1000]"><AuthLoadingScreen failed={state === "failed"} /></div>
}

export function AuthReadySignal({ children }: { children: ReactNode }) {
  useEffect(() => {
    const atlasWindow = window as AtlasWindow
    atlasWindow.__atlasAuthReady = true
    atlasWindow.__atlasAuthFailed = false
    window.dispatchEvent(new Event(READY_EVENT))
  }, [])

  return children
}

export function AuthFailedSignal() {
  useEffect(() => {
    const atlasWindow = window as AtlasWindow
    atlasWindow.__atlasAuthFailed = true
    window.dispatchEvent(new Event(FAILED_EVENT))
  }, [])

  return null
}
